import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Plus } from "lucide-react";
import { rupiah, tanggal, todayISO } from "@/lib/format";

export default function Payments() {
  const { data, loading, reload } = useFetch("/payments");
  const { data: clients } = useFetch("/clients");
  const { data: invoices } = useFetch("/invoices");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: todayISO(), method: "Transfer" });
  const [saving, setSaving] = useState(false);

  const clientName = (id) => (clients || []).find((c) => c.id === id)?.name || "-";
  const invOptions = (invoices || []).filter((i) => i.client_id === form.client_id && !["Draft", "Dibatalkan", "Lunas"].includes(i.status));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.client_id || !form.amount) { toast.error("Klien & nilai wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount) });
      toast.success("Pembayaran dicatat"); setOpen(false); setForm({ date: todayISO(), method: "Transfer" }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Pembayaran" subtitle="Catat pembayaran & alokasi ke invoice"
        action={<Button onClick={() => setOpen(true)} data-testid="payment-add-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><Plus className="h-4 w-4 mr-1" /> Catat Pembayaran</Button>} />
      {loading ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <DataTable testId="payments" data={data || []} columns={[
          { key: "number", label: "No. Pembayaran" },
          { key: "date", label: "Tanggal", render: (r) => tanggal(r.date) },
          { key: "client_id", label: "Klien", render: (r) => clientName(r.client_id) },
          { key: "amount", label: "Nilai", numeric: true, render: (r) => rupiah(r.amount) },
          { key: "method", label: "Metode" },
          { key: "allocated", label: "Alokasi", render: (r) => {
            const tone = r.allocated ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200";
            return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${tone}`}>{r.allocated ? "Teralokasi" : "Uang Muka"}</span>;
          } },
        ]} />
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto scrollbar-thin">
          <SheetHeader><SheetTitle className="font-display">Catat Pembayaran</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <F label="Klien"><Select value={form.client_id || ""} onValueChange={(v) => set("client_id", v)}><SelectTrigger data-testid="pay-client"><SelectValue placeholder="Pilih klien" /></SelectTrigger><SelectContent>{(clients || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></F>
            <F label="Invoice (opsional — kosongkan untuk uang muka)"><Select value={form.invoice_id || ""} onValueChange={(v) => set("invoice_id", v)}><SelectTrigger data-testid="pay-invoice"><SelectValue placeholder="Pilih invoice" /></SelectTrigger><SelectContent>{invOptions.map((i) => <SelectItem key={i.id} value={i.id}>{i.number} — {rupiah(i.grand_total - (i.paid_amount || 0))}</SelectItem>)}</SelectContent></Select></F>
            <F label="Tanggal"><Input type="date" data-testid="pay-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></F>
            <F label="Nilai Pembayaran (Rp)"><Input type="number" data-testid="pay-amount" value={form.amount || ""} onChange={(e) => set("amount", e.target.value)} /></F>
            <F label="Metode"><Select value={form.method} onValueChange={(v) => set("method", v)}><SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Transfer">Transfer Bank</SelectItem><SelectItem value="Tunai">Tunai</SelectItem><SelectItem value="Giro">Giro/Cek</SelectItem></SelectContent></Select></F>
            <F label="Referensi Bank"><Input data-testid="pay-ref" value={form.bank_ref || ""} onChange={(e) => set("bank_ref", e.target.value)} /></F>
          </div>
          <SheetFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="payment-save-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]">{saving ? "..." : "Simpan"}</Button></SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
function F({ label, children }) { return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>; }
